import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  supabase,
  isSupabaseConfigured,
  classifySupabaseError,
  isDatabaseUnreachableError,
  reportSchemaError,
} from 'lib/supabase';

const STORAGE_KEY = 'edutracker.students.v1';

// Separate key for the live-mode snapshot cache. We deliberately don't
// reuse STORAGE_KEY (demo mode's authoritative store) so that a browser
// that has been used in both modes never blends the two roster lists.
// Bumping the ".v1" suffix invalidates every cache when the shape
// changes, same convention as STORAGE_KEY.
const LIVE_SNAPSHOT_KEY = 'edutracker.students.livecache.v1';

const TABLE = 'students';

// Columns added in migration 0010. If the user hasn't run 0010 on their
// Supabase project yet, an INSERT/UPDATE that includes any of these
// fails with PGRST204 "Could not find the 'x' column". We detect that
// case at write time, strip the 0010 columns from the payload, and
// retry so the base add-student flow still succeeds. See
// stripMigration0010Columns() below.
const MIGRATION_0010_COLUMNS = new Set([
  'emergency_contact',
  'accommodations',
  'secondary_disabilities',
  'service_providers',
  'medical_info',
  'behavioral_notes',
  'academic_level',
  'communication_method',
  'transportation_needs',
  'dietary_restrictions',
]);

// Columns added in migration 0008. Same graceful-degradation story.
const MIGRATION_0008_COLUMNS = new Set([
  'iep_start_date',
  'iep_annual_review_due',
]);

function stripMigration0010Columns(row) {
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    if (MIGRATION_0010_COLUMNS.has(k)) continue;
    out[k] = v;
  }
  return out;
}

function stripMigration0008Columns(row) {
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    if (MIGRATION_0008_COLUMNS.has(k)) continue;
    out[k] = v;
  }
  return out;
}

const StudentsContext = createContext(null);

function generateId() {
  const year = new Date().getFullYear();
  const rand = Math.random().toString(36).slice(2, 7).toUpperCase();
  return `STU-${year}-${rand}`;
}

// Small helper so a legacy row that has scalar emergency-contact fields
// on it still lifts up into the { name, relationship, phone, email }
// shape the UI expects. New writes always store the jsonb object.
function coerceEmergencyContact(row) {
  const raw = row.emergency_contact ?? row.emergencyContact ?? null;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return {
      name: raw.name ?? '',
      relationship: raw.relationship ?? '',
      phone: raw.phone ?? '',
      email: raw.email ?? '',
    };
  }
  return null;
}

function coerceStringArray(value) {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
  return [];
}

function normalizeIncoming(row) {
  if (!row) return null;
  return {
    id: row.id,
    firstName: row.first_name ?? row.firstName ?? '',
    lastName: row.last_name ?? row.lastName ?? '',
    grade: row.grade ?? '',
    dateOfBirth: row.date_of_birth ?? row.dateOfBirth ?? null,
    primaryDisability: row.primary_disability ?? row.primaryDisability ?? '',
    photo: row.photo ?? null,
    notes: row.notes ?? '',
    status: row.status ?? 'active',
    // IEP review dates. Both nullable — a student can exist in the roster
    // before their IEP paperwork lands.
    iepStartDate:
      row.iep_start_date ?? row.iepStartDate ?? null,
    iepAnnualReviewDue:
      row.iep_annual_review_due ?? row.iepAnnualReviewDue ?? null,
    // Fields added by migration 0010. Every one is optional and defaults
    // to a shape (null / '' / []) that the form's <Input>/<Select> can
    // consume without special-casing.
    emergencyContact:      coerceEmergencyContact(row),
    accommodations:        row.accommodations ?? row.accommodationsText ?? '',
    secondaryDisabilities: coerceStringArray(row.secondary_disabilities ?? row.secondaryDisabilities),
    serviceProviders:      coerceStringArray(row.service_providers      ?? row.serviceProviders),
    medicalInfo:           row.medical_info          ?? row.medicalInfo         ?? '',
    behavioralNotes:       row.behavioral_notes      ?? row.behavioralNotes     ?? '',
    academicLevel:         row.academic_level        ?? row.academicLevel       ?? '',
    communicationMethod:   row.communication_method  ?? row.communicationMethod ?? '',
    transportationNeeds:   row.transportation_needs  ?? row.transportationNeeds ?? '',
    dietaryRestrictions:   row.dietary_restrictions  ?? row.dietaryRestrictions ?? '',
    createdAt: row.created_at ?? row.createdAt ?? null,
    lastUpdated: row.last_updated ?? row.lastUpdated ?? null,
  };
}

// Trim a shallow object's string values, drop empties, and return null if
// the whole thing is empty. Prevents "{}" contact rows from cluttering
// the DB when a teacher tabs through the fields without filling any in.
function shallowCleanObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const out = {};
  let hasSomething = false;
  for (const [k, v] of Object.entries(value)) {
    const trimmed = typeof v === 'string' ? v.trim() : v;
    if (trimmed === '' || trimmed == null) continue;
    out[k] = trimmed;
    hasSomething = true;
  }
  return hasSomething ? out : null;
}

function normalizeStringArray(value) {
  if (!Array.isArray(value)) return [];
  const out = value
    .map((v) => (typeof v === 'string' ? v.trim() : String(v || '').trim()))
    .filter(Boolean);
  return out.length ? out : [];
}

// Full row -- for INSERT. Every column gets a value so NOT NULL columns
// (first_name / last_name / status) always have one.
function toRow(student) {
  const emergency = shallowCleanObject(student.emergencyContact);
  const secondary = normalizeStringArray(student.secondaryDisabilities);
  const services  = normalizeStringArray(student.serviceProviders);
  return {
    id: student.id,
    first_name: student.firstName ?? '',
    last_name: student.lastName ?? '',
    grade: student.grade ?? null,
    date_of_birth: student.dateOfBirth || null,
    primary_disability: student.primaryDisability || null,
    photo: student.photo || null,
    notes: student.notes || null,
    status: student.status || 'active',
    iep_start_date: student.iepStartDate || null,
    iep_annual_review_due: student.iepAnnualReviewDue || null,
    emergency_contact:      emergency,
    accommodations:         (student.accommodations || '').trim() || null,
    secondary_disabilities: secondary.length ? secondary : null,
    service_providers:      services.length ? services : null,
    medical_info:           (student.medicalInfo || '').trim()         || null,
    behavioral_notes:       (student.behavioralNotes || '').trim()     || null,
    academic_level:         (student.academicLevel || '').trim()       || null,
    communication_method:   (student.communicationMethod || '').trim() || null,
    transportation_needs:   (student.transportationNeeds || '').trim() || null,
    dietary_restrictions:   (student.dietaryRestrictions || '').trim() || null,
  };
}

// Partial row -- for UPDATE. Only include the columns the caller
// explicitly touched. The previous version defaulted every unspecified
// field, which caused a photo-only update to silently blank out the
// student's name and grade with empty strings. We map camelCase to
// snake_case one field at a time and skip anything the patch doesn't
// mention.
function toPatchRow(patch) {
  const row = {};
  if (!patch || typeof patch !== 'object') return row;
  if ('firstName' in patch) row.first_name = patch.firstName ?? '';
  if ('lastName' in patch) row.last_name = patch.lastName ?? '';
  if ('grade' in patch) row.grade = patch.grade ?? null;
  if ('dateOfBirth' in patch) row.date_of_birth = patch.dateOfBirth || null;
  if ('primaryDisability' in patch)
    row.primary_disability = patch.primaryDisability || null;
  if ('photo' in patch) row.photo = patch.photo || null;
  if ('notes' in patch) row.notes = patch.notes || null;
  if ('status' in patch) row.status = patch.status || 'active';
  if ('iepStartDate' in patch)
    row.iep_start_date = patch.iepStartDate || null;
  if ('iepAnnualReviewDue' in patch)
    row.iep_annual_review_due = patch.iepAnnualReviewDue || null;

  // Migration 0010 fields. Empty objects / empty arrays / empty strings
  // all normalize to null so the DB never stores placeholder values.
  if ('emergencyContact' in patch) {
    row.emergency_contact = shallowCleanObject(patch.emergencyContact);
  }
  if ('accommodations' in patch) {
    row.accommodations = (patch.accommodations || '').trim() || null;
  }
  if ('secondaryDisabilities' in patch) {
    const arr = normalizeStringArray(patch.secondaryDisabilities);
    row.secondary_disabilities = arr.length ? arr : null;
  }
  if ('serviceProviders' in patch) {
    const arr = normalizeStringArray(patch.serviceProviders);
    row.service_providers = arr.length ? arr : null;
  }
  if ('medicalInfo' in patch)
    row.medical_info = (patch.medicalInfo || '').trim() || null;
  if ('behavioralNotes' in patch)
    row.behavioral_notes = (patch.behavioralNotes || '').trim() || null;
  if ('academicLevel' in patch)
    row.academic_level = (patch.academicLevel || '').trim() || null;
  if ('communicationMethod' in patch)
    row.communication_method = (patch.communicationMethod || '').trim() || null;
  if ('transportationNeeds' in patch)
    row.transportation_needs = (patch.transportationNeeds || '').trim() || null;
  if ('dietaryRestrictions' in patch)
    row.dietary_restrictions = (patch.dietaryRestrictions || '').trim() || null;

  return row;
}

function readLocal() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocal(students) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(students));
  } catch {
    // Storage may be full or unavailable (e.g. Safari private mode) —
    // fall back silently, the in-memory state still works for the session.
  }
}

// ---------------------------------------------------------------------------
// Live-mode snapshot cache
//
// Every successful load from Supabase mirrors the roster (already
// normalized to the UI shape) into localStorage. On a subsequent page
// load or new tab, if Supabase is unreachable — the single scariest
// case being the free-tier project auto-pausing after 7 days of no
// activity — we can hydrate the initial render from the cached
// snapshot instead of showing an empty roster. The UI surfaces a
// friendly "showing your last-known roster, database is asleep" banner
// while a background refresh retries.
//
// Privacy note: this cache respects the app's existing threat model.
// The row-level-security policy in migration 0001_students.sql grants
// every authenticated user read access to every students row, so two
// signed-in users on the same browser would see the same roster from
// the server anyway. We still clear this cache on sign-out (see the
// auth listener in StudentsProvider) so a shared-device sign-out
// doesn't leave the roster visible to a fully signed-out visitor.
// ---------------------------------------------------------------------------

function readLiveSnapshot() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LIVE_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.students)) return null;
    return {
      students: parsed.students,
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : null,
    };
  } catch {
    return null;
  }
}

function writeLiveSnapshot(students) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      LIVE_SNAPSHOT_KEY,
      JSON.stringify({ students, savedAt: Date.now() }),
    );
  } catch {
    // localStorage full / disabled — the offline fallback simply won't
    // be available for the next reload; the in-memory list still works
    // for this session. Nothing scary to surface to the teacher.
  }
}

function clearLiveSnapshot() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LIVE_SNAPSHOT_KEY);
  } catch {
    // ignore
  }
}

export function StudentsProvider({ children }) {
  const supabaseLive = isSupabaseConfigured() && supabase !== null;

  // Hydrate the initial roster from whichever source matches the
  // current mode:
  //   * Demo mode -> the authoritative localStorage list.
  //   * Live mode -> the last-known cached snapshot from a previous
  //     successful load, so a paused / offline Supabase never renders
  //     an empty roster on the first paint. If no cache exists yet,
  //     we start with [] and let the network fetch populate it.
  const initialSnapshot = supabaseLive ? readLiveSnapshot() : null;
  const [students, setStudents] = useState(() =>
    supabaseLive ? initialSnapshot?.students ?? [] : readLocal(),
  );
  const [loading, setLoading] = useState(supabaseLive);
  const [error, setError] = useState(null);
  // True when the currently displayed roster came from the cached
  // snapshot rather than a live Supabase fetch. Drives the roster
  // page's "showing your last-known roster" banner.
  const [usingCachedSnapshot, setUsingCachedSnapshot] = useState(
    Boolean(initialSnapshot?.students?.length),
  );
  // True when the last live fetch failed because we couldn't reach the
  // database at all (paused project / offline / 5xx). Distinct from
  // `error`, which we keep for the schema-error path.
  const [unreachable, setUnreachable] = useState(false);
  // Timestamp (ms) of the cached snapshot we're currently showing, so
  // the UI can say "last synced 2 hours ago" instead of just "cached".
  const [snapshotSavedAt, setSnapshotSavedAt] = useState(
    initialSnapshot?.savedAt ?? null,
  );
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!supabaseLive) {
      setStudents(readLocal());
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from(TABLE)
      .select('*')
      .order('last_updated', { ascending: false });
    if (!mounted.current) return;
    if (fetchError) {
      // A missing students table means the Supabase project is
      // completely un-migrated. Kick to the schema-error pub/sub so
      // the top-of-app banner shows the setup instructions.
      const classified = classifySupabaseError(fetchError);
      if (classified?.kind === 'missing_table') {
        reportSchemaError(fetchError, 'students.load');
      }

      // Paused-project / network / 5xx: don't blow away whatever
      // roster we're already showing (either the last successful
      // in-memory list or the hydrated snapshot). Just flag the
      // outage so the UI can swap the scary red "Failed to load"
      // banner for the friendly amber "database is asleep" one.
      if (isDatabaseUnreachableError(fetchError)) {
        setUnreachable(true);
        const snap = readLiveSnapshot();
        if (snap?.students?.length) {
          setStudents(snap.students);
          setUsingCachedSnapshot(true);
          setSnapshotSavedAt(snap.savedAt);
        }
        // Deliberately leave `error` null so the roster page's
        // schema-flavored red banner doesn't appear — the paused-DB
        // banner is a much better fit and will render off `unreachable`.
        setLoading(false);
        return;
      }

      setError(fetchError.message || 'Failed to load students');
      setLoading(false);
      return;
    }

    const list = (data || []).map(normalizeIncoming).filter(Boolean);
    setStudents(list);
    setUsingCachedSnapshot(false);
    setUnreachable(false);
    setSnapshotSavedAt(Date.now());
    writeLiveSnapshot(list);
    setLoading(false);
  }, [supabaseLive]);

  useEffect(() => {
    if (!supabaseLive) return undefined;
    refresh();
    const channel = supabase
      .channel('public:students')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: TABLE },
        () => {
          refresh();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabaseLive, refresh]);

  useEffect(() => {
    if (supabaseLive) return undefined;
    const onStorage = (event) => {
      if (event.key === STORAGE_KEY) {
        setStudents(readLocal());
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
    return undefined;
  }, [supabaseLive]);

  // Wipe the offline snapshot when the user signs out. Protects
  // shared-device scenarios (e.g. a chromebook cart) from leaving the
  // roster visible to whoever opens the tab next. Sign-in populates
  // the cache again on the first successful refresh.
  useEffect(() => {
    if (!supabaseLive || !supabase) return undefined;
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        clearLiveSnapshot();
        if (!mounted.current) return;
        setStudents([]);
        setUsingCachedSnapshot(false);
        setUnreachable(false);
        setSnapshotSavedAt(null);
      }
    });
    return () => {
      try { sub?.subscription?.unsubscribe(); } catch { /* ignore */ }
    };
  }, [supabaseLive]);

  // Auto-retry when we know the DB is unreachable. Supabase's free-tier
  // wake-from-pause takes 30-60 seconds from the moment a request hits
  // it. This effect keeps quietly probing every 8 seconds so that once
  // the DB comes back — whether from the user clicking the dashboard
  // "Restore project" button, from the scheduled keep-alive workflow
  // running, or from the network coming back — the roster refreshes
  // and the amber banner disappears with no user action. Interval is
  // deliberately conservative to avoid piling requests onto a
  // still-recovering DB.
  useEffect(() => {
    if (!supabaseLive) return undefined;
    if (!unreachable) return undefined;
    let cancelled = false;
    const id = setInterval(() => {
      if (cancelled) return;
      // Fire-and-forget: refresh() itself is idempotent and will flip
      // `unreachable` false on success, causing this effect to tear
      // down. If it fails again, nothing happens — the next tick
      // retries.
      refresh().catch(() => { /* handled inside refresh() */ });
    }, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [supabaseLive, unreachable, refresh]);

  const addStudent = useCallback(
    async (input) => {
      const nowIso = new Date().toISOString();
      const draft = normalizeIncoming({
        id: input?.id || generateId(),
        first_name: input?.firstName || '',
        last_name: input?.lastName || '',
        grade: input?.grade || '',
        date_of_birth: input?.dateOfBirth || null,
        primary_disability: input?.primaryDisability || '',
        photo: input?.photo || null,
        notes: input?.notes || '',
        status: input?.status || 'active',
        iep_start_date: input?.iepStartDate || null,
        iep_annual_review_due: input?.iepAnnualReviewDue || null,
        // Fields from migration 0010 — normalizeIncoming coerces these
        // through the same helpers a Supabase-loaded row goes through,
        // so demo mode and live mode produce identical shapes.
        emergency_contact:      shallowCleanObject(input?.emergencyContact),
        accommodations:         input?.accommodations,
        secondary_disabilities: input?.secondaryDisabilities,
        service_providers:      input?.serviceProviders,
        medical_info:           input?.medicalInfo,
        behavioral_notes:       input?.behavioralNotes,
        academic_level:         input?.academicLevel,
        communication_method:   input?.communicationMethod,
        transportation_needs:   input?.transportationNeeds,
        dietary_restrictions:   input?.dietaryRestrictions,
        created_at: nowIso,
        last_updated: nowIso,
      });

      if (!supabaseLive) {
        const next = [draft, ...readLocal()];
        writeLocal(next);
        setStudents(next);
        return draft;
      }

      // Graceful degradation: if the Supabase project hasn't had
      // migrations 0008 / 0010 applied, retry the insert with the
      // affected columns stripped so at least the base add-student
      // flow works during a district demo. We still push the error
      // through reportSchemaError so the SchemaSetupBanner tells the
      // user to run the setup script for full functionality.
      let row = toRow(draft);
      let attempt = 0;
      let saved = null;
      while (attempt < 3) {
        const { data, error: insertError } = await supabase
          .from(TABLE)
          .insert(row)
          .select('*')
          .single();
        if (!insertError) {
          saved = normalizeIncoming(data);
          break;
        }
        const classified = classifySupabaseError(insertError);
        if (classified?.kind === 'missing_column') {
          reportSchemaError(insertError, 'students.insert');
          const col = classified.column;
          if (MIGRATION_0010_COLUMNS.has(col)) {
            row = stripMigration0010Columns(row);
            attempt += 1;
            continue;
          }
          if (MIGRATION_0008_COLUMNS.has(col)) {
            row = stripMigration0008Columns(row);
            attempt += 1;
            continue;
          }
        }
        setError(insertError.message || 'Failed to add student');
        throw insertError;
      }
      if (!saved) {
        // Ran out of retry slots. Fall back to reading the draft we
        // *tried* to save so the UI at least reflects the base fields.
        saved = draft;
      }
      setStudents((prev) => [saved, ...prev.filter((s) => s.id !== saved.id)]);
      return saved;
    },
    [supabaseLive]
  );

  const updateStudent = useCallback(
    async (id, patch) => {
      const nowIso = new Date().toISOString();

      if (!supabaseLive) {
        const current = readLocal();
        const next = current.map((s) =>
          s.id === id ? { ...s, ...patch, lastUpdated: nowIso } : s
        );
        writeLocal(next);
        setStudents(next);
        return next.find((s) => s.id === id) || null;
      }

      // Same graceful-degradation loop as addStudent — if the update
      // touches a 0008 / 0010 column that hasn't been migrated in yet,
      // retry without that column so a photo change / rename still
      // succeeds. Meanwhile the SchemaSetupBanner nags the user to
      // run the setup script for the fields we had to drop.
      let row = toPatchRow(patch);
      let attempt = 0;
      let saved = null;
      while (attempt < 3) {
        const { data, error: updateError } = await supabase
          .from(TABLE)
          .update(row)
          .eq('id', id)
          .select('*')
          .single();
        if (!updateError) {
          saved = normalizeIncoming(data);
          break;
        }
        const classified = classifySupabaseError(updateError);
        if (classified?.kind === 'missing_column') {
          reportSchemaError(updateError, 'students.update');
          const col = classified.column;
          if (MIGRATION_0010_COLUMNS.has(col)) {
            row = stripMigration0010Columns(row);
            attempt += 1;
            continue;
          }
          if (MIGRATION_0008_COLUMNS.has(col)) {
            row = stripMigration0008Columns(row);
            attempt += 1;
            continue;
          }
        }
        // Not a schema issue — surface the raw error to the caller.
        throw updateError;
      }
      if (!saved) throw new Error('Update failed after retrying without unmigrated columns.');
      setStudents((prev) => prev.map((s) => (s.id === id ? saved : s)));
      return saved;
    },
    [supabaseLive]
  );

  const removeStudent = useCallback(
    async (id) => {
      if (!supabaseLive) {
        const next = readLocal().filter((s) => s.id !== id);
        writeLocal(next);
        setStudents(next);
        return true;
      }
      const { error: deleteError } = await supabase.from(TABLE).delete().eq('id', id);
      if (deleteError) {
        // Same reasoning as updateStudent: don't pollute the load-time
        // error banner with a delete-time failure.
        throw deleteError;
      }
      setStudents((prev) => prev.filter((s) => s.id !== id));
      return true;
    },
    [supabaseLive]
  );

  const getStudent = useCallback((id) => students.find((s) => s.id === id) || null, [students]);

  const searchStudents = useCallback(
    (query) => {
      const q = (query || '').trim().toLowerCase();
      if (!q) return students;
      return students.filter((s) => {
        const name = `${s.firstName} ${s.lastName}`.toLowerCase();
        return (
          name.includes(q) ||
          (s.id || '').toLowerCase().includes(q) ||
          (String(s.grade || '')).toLowerCase().includes(q)
        );
      });
    },
    [students]
  );

  const value = useMemo(
    () => ({
      students,
      loading,
      error,
      isLive: supabaseLive,
      // "The database was unreachable on the last load attempt." UI uses
      // this to swap the generic red error banner for a friendly amber
      // "your database is asleep, here's the wake-up link" banner.
      unreachable,
      // "The roster on screen came from the offline snapshot, not from
      // a live query." UI uses this to show a subtle "showing your
      // last-known roster" indicator + a Try Again button.
      usingCachedSnapshot,
      // Timestamp of the cached snapshot (ms since epoch), so the UI
      // can say "last synced 3h ago" instead of just "cached".
      snapshotSavedAt,
      refresh,
      addStudent,
      updateStudent,
      removeStudent,
      getStudent,
      searchStudents,
    }),
    [
      students,
      loading,
      error,
      supabaseLive,
      unreachable,
      usingCachedSnapshot,
      snapshotSavedAt,
      refresh,
      addStudent,
      updateStudent,
      removeStudent,
      getStudent,
      searchStudents,
    ]
  );

  return <StudentsContext.Provider value={value}>{children}</StudentsContext.Provider>;
}

export function useStudents() {
  const ctx = useContext(StudentsContext);
  if (!ctx) {
    throw new Error('useStudents must be used within a <StudentsProvider>');
  }
  return ctx;
}
